import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'sb-creator-auth',
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

// Mirror the current access token into a synchronously-readable variable.
// `supabase.auth.getSession()` is async, but some hot paths (e.g. firing a
// keepalive click beacon in a click handler that is about to open a new tab /
// unload) must dispatch the request synchronously so it survives page
// teardown. We keep this cache fresh via the auth state subscription.
let cachedAccessToken: string | null = null;
void supabase.auth.getSession().then(({ data }) => {
  cachedAccessToken = data.session?.access_token ?? null;
});
supabase.auth.onAuthStateChange((_event, session) => {
  cachedAccessToken = session?.access_token ?? null;
});

/** Current Supabase access token if a session exists, else null. Synchronous. */
export function getCachedAccessToken(): string | null {
  return cachedAccessToken;
}

export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseAnonKey;

const CREATOR_HANDOFF_RESTORE_URL = '/auth/sso/creator-restore';

/**
 * Restore the signed-in shoutout.us session for an explicit dashboard handoff.
 * The session cookie remains HttpOnly; the creator server validates it before
 * returning short-lived Supabase session tokens to this same-origin client.
 */
export async function restoreMainAppSsoSession(expectedUserId?: string): Promise<boolean> {
  const { data: current } = await supabase.auth.getSession();
  if (current.session?.user?.id && current.session.user.id === expectedUserId) {
    return true;
  }

  try {
    const response = await fetch(CREATOR_HANDOFF_RESTORE_URL, {
      method: 'GET',
      credentials: 'same-origin',
    });
    if (!response.ok) return false;

    const tokens = await response.json();
    if (!tokens?.access_token || !tokens?.refresh_token) return false;
    if (expectedUserId && tokens?.user_id !== expectedUserId) return false;

    const { data, error } = await supabase.auth.setSession({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
    });
    if (error || !data.session?.user) return false;

    return !expectedUserId || data.session.user.id === expectedUserId;
  } catch (_) {
    return false;
  }
}
